import {TreeNode} from "primeng/api";
import {MetaDataAttributeDto} from "../../meta/dto/meta-data-attribute.dto";
import {DataTypeHelper} from "../../helper/data-type.helper";
import {FilterAttribute} from "../api/filter-attribute";
import {FilterAttributeGroup} from "../api/filter-attribute-group";
import {MvsFormFieldDto} from "../../dto/form/dto/mvs-form-field.dto";
import {MvsFormDto} from "../../dto/form/dto/mvs-form.dto";
import {FilterAttributeValues} from "../api/filter-attribute-values";
import {WidgetData} from "../../widget/widget-data";
import {QlRequestDto} from "../../ql/data/dto/ql-request.dto";
import {QlQueryStartDto} from "../../ql/data/dto/ql-query-start.dto";
import {QlJoinDto} from "../../ql/data/dto/ql-join.dto";

export class MvsFilterHelper {

  public static convertToAttributeGroup(attributeList: MetaDataAttributeDto[]): FilterAttributeGroup[] {
    let filterAttributes = [];

    attributeList.forEach((entry: any) => {

        let dataType: number;
        if (entry.dataType) {

          dataType = DataTypeHelper.getInternalDataTypeForFormField(entry);
        }
        const objectTypeAlias: string = entry.joinObjectTypeAlias ? entry.joinObjectTypeAlias : null;

        const objectTypeId: number = entry.joinObjectTypeId ? entry.joinObjectTypeId : null;

        const valueListEntries: any = entry.valueList ? entry.valueListEntries : [];

        const label = entry.label ? entry.label : entry.attributeName;

        const filterAttributesWithValues = FilterAttribute.create(entry.attributeName, label, dataType, objectTypeAlias, objectTypeId, valueListEntries);

        filterAttributes.push(filterAttributesWithValues);
      }
    );

    return [FilterAttributeGroup.create("Attributes", "Attributes", filterAttributes)];

  }

  public static convertFormFieldsToAttributeGroup(formFields: MvsFormFieldDto[]): FilterAttributeGroup[] {
    const filterAttributes: FilterAttribute[] = [];

    for (let item in formFields) {

      if (item.endsWith('DtoId')) {
        continue;
      }

      const field = formFields[item];

      let isJoin = false;

      if (field.relationship === "OneToMany" || field.relationship === "ManyToOne") {
        isJoin = true;
      }

      const attribute: FilterAttribute = {
        attribute: field.id,
        dataType: field.dataType ? DataTypeHelper.getInternalDataType(field.dataType) : null,
        label: field.uiLabel || field.id,
        values: this.getValuesList(field),
        variables: [],
        uiType: isJoin ? 'join' : 'attribute'
      };

      filterAttributes.push(attribute);
    }

    return [FilterAttributeGroup.create("Attributes", "Attributes", filterAttributes)];
  }

  private static getValuesList(field: MvsFormFieldDto): FilterAttributeValues[] {
    return field.valueList?.entries?.map(entry => ({
      key: entry.key,
      label: entry.label,
      technicalLabel: entry.technicalKey,
      value: entry.key
    })) || [];
  }

  public static buildCascadeSelectNodes(form: MvsFormDto, widgetData: WidgetData): TreeNode[] {
    let rootNode: TreeNode;
    if (widgetData.dataSource == 'entity') {
      rootNode = {
        label: form.objectType,
        expanded: false,
        selectable: false,
        children: this.createEntityNodes(form, '', widgetData),
        leaf: false
      };
    } else {
      const qlHierarchy = this.extractQlEntityHierarchy(widgetData, form);
      const fieldsByAlias = this.groupFormFieldsByAlias(form);

      rootNode = this.buildTreeNodeFromQlHierarchy(
          qlHierarchy,
          fieldsByAlias,
          form
      );
    }

    return [rootNode];
  }

  public static createEntityNodes(form: MvsFormDto, prefix: string = '', widgetData: WidgetData): TreeNode[] {
    const nodes: any[] = [];

    for (const fieldKey in form.formFields) {
      const uiObject = MvsFilterHelper.convertAttributeToFilterAttribute(fieldKey, form, prefix);
      if (uiObject) {
        nodes.push(uiObject);
      }
    }

    for (const subFormKey in form.subForms) {
      // if (subFormKey.endsWith('Dto')) {
      //   //if we have person and personDto, we prefer person subform
      //   continue;
      // }
      const subForm = form.subForms[subFormKey];
      const joinedPrefix = `${prefix}${subFormKey}.`;

      const exists = nodes.find(item => item.label == subForm.objectType && item.leaf == false);
      if (exists) {
        // we might get two objects with same object type (eg person & personDto), in that case we just push one
        continue;
      }

      nodes.push({
        label: subForm.objectType,
        expanded: false,
        lazy: true,
        selectable: false,
        data: {
          joinedAttribute: subFormKey
        },
        children: [],
        leaf: false
      });
    }

    return nodes;
  }

  public static convertAttributeToFilterAttribute(fieldKey: string, form: MvsFormDto, prefix: string = '') {
    if (!fieldKey || fieldKey == '') {
      return null;
    }
    let field = form.formFields[fieldKey];

    if (fieldKey && !field) {
      // checking if its a oneToMany field since we dont add DtoId when saving the field
      const fieldName = fieldKey + 'DtoId';
      field = form.formFields[fieldName];
    }

    if (field.id.endsWith('DtoName') || field.id.endsWith('DtoColorBackground') || field.id.endsWith('DtoImage') || field.id.endsWith('DtoColor')) {
      return null;
    }

    let fieldName = fieldKey;
    if (fieldName.endsWith('DtoId')) {
      fieldName = fieldName.replace('DtoId','');
    }

    if (field.uiLabel.endsWith('DtoId')) {
      field.uiLabel = field.uiLabel.replace('DtoId','');
    }

    let isJoin: boolean = false;
    if (field.relationship === "OneToMany" || field.relationship === "ManyToOne") {
      isJoin = true;
    }

    // return {
    //   label: `${prefix}${fieldName}`,
    //   data: {
    //     attribute: `${prefix}${fieldKey}`,
    //     dataType: field.dataType ? DataTypeHelper.getInternalDataType(field.dataType) : null,
    //     uiType: field.uiInputControl,
    //     values: this.getValuesList(field),
    //     isJoin: isJoin,
    //     objectType: field.dataType
    //   },
    //   leaf: true
    // }

    let resolvedFieldPath: string;

    if (prefix && fieldName.startsWith(prefix)) {
      resolvedFieldPath = fieldName;
    } else {
      resolvedFieldPath = prefix ? prefix + fieldName : fieldName;
    }

    return {
      key: resolvedFieldPath,
      label: `${field.uiLabel} (${field.id})`,
      data: {
        field: resolvedFieldPath,
        dataType: field.dataType ? DataTypeHelper.getInternalDataType(field.dataType) : null,
        uiType: field.uiInputControl,
        values: this.getValuesList(field),
        isJoin: isJoin,
        objectType: isJoin ? field.referenceEntity : field.dataType,
        joinedAttribute: prefix
      },
      leaf: true
    }
  }

  private static extractQlEntityHierarchy(widgetData: WidgetData, form: MvsFormDto) {
    const request = widgetData.dataProviderListRequest as QlRequestDto;
    const query = request.queries[0];

    return this.mapQlStartToHierarchyNode(query.start, form);
  }

  private static mapQlStartToHierarchyNode(start: QlQueryStartDto, form: MvsFormDto) {

    const joins: QlJoinDto[] = [];

    if (start.joins) {
      for (const join of start.joins) {
        joins.push(this.mapQlJoinToHierarchyNode(join, form));
      }
    }

    return {
      entityName: start.name,
      alias: start.as,
      joins: joins
    };
  }

  private static mapQlJoinToHierarchyNode(join: QlJoinDto, form: MvsFormDto) {

    const childJoins: QlJoinDto[] = [];

    if (join.joins) {
      for (const child of join.joins) {
        childJoins.push(this.mapQlJoinToHierarchyNode(child, form));
      }
    }
    const formContent = Object.values(form.formContents).find((fc) => fc.as == join.as);
    let entityName = join.name;
    if (formContent) {
        entityName = formContent.objectType;
    }

    return {
      entityName: entityName,
      alias: join.as,
      joins: childJoins
    };
  }

  private static groupFormFieldsByAlias(
      form: MvsFormDto
  ): Map<string, MvsFormFieldDto[]> {

    const fieldsByAlias = new Map<string, MvsFormFieldDto[]>();
    const fields = Object.values(form.formFields);

    for (const field of fields) {
      const alias = this.resolveFieldAlias(field);

      if (!fieldsByAlias.has(alias)) {
        fieldsByAlias.set(alias, []);
      }

      fieldsByAlias.get(alias)!.push(field);
    }

    return fieldsByAlias;
  }

  private static resolveFieldAlias(
      field: MvsFormFieldDto
  ): string {
    let alias: string = '';

    if (field.id.includes('.')) {
      const parts = field.id.split('.');
      alias = parts[0];
    }
    return alias;
  }

  private static buildTreeNodeFromQlHierarchy(
      hierarchyNode: any,
      fieldsByAlias: Map<string, MvsFormFieldDto[]>,
      form: MvsFormDto,
      visitedAliases?: Set<string>
  ): TreeNode {

    const isRoot = !visitedAliases;
    if (isRoot) {
      visitedAliases = new Set<string>();
    }
    visitedAliases.add(hierarchyNode.alias);

    const fieldNodes = this.buildFieldNodesForAlias(
        hierarchyNode.alias,
        fieldsByAlias,
        form
    );

    const childEntityNodes: TreeNode[] = [];

    for (const child of hierarchyNode.joins) {
      childEntityNodes.push(
          this.buildTreeNodeFromQlHierarchy(child, fieldsByAlias, form, visitedAliases)
      );
    }

    // At root level: add nodes for backend-generated FK aliases not in explicit joins
    if (isRoot) {
      for (const [alias, fields] of fieldsByAlias) {
        if (visitedAliases.has(alias)) continue;

        const fcEntry = form.formContents
            ? Object.values(form.formContents).find((fc: any) => fc.as === alias)
            : null;
        const entityName = fcEntry ? (fcEntry as any).objectType : alias;

        const fkFieldNodes = this.buildFieldNodesForAlias(alias, fieldsByAlias, form);
        childEntityNodes.push({
          key: '_entity_' + alias,
          label: entityName,
          expanded: false,
          selectable: false,
          children: fkFieldNodes,
          leaf: false
        });
      }
    }

    return {
      key: '_entity_' + hierarchyNode.alias,
      label: hierarchyNode.entityName,
      expanded: false,
      selectable: false,
      children: fieldNodes.concat(childEntityNodes),
      leaf: false
    };
  }

  private static buildFieldNodesForAlias(
      alias: string,
      fieldsByAlias: Map<string, MvsFormFieldDto[]>,
      form: MvsFormDto
  ): TreeNode[] {

    const nodes: TreeNode[] = [];
    const fields = fieldsByAlias.get(alias) || [];

    for (const field of fields) {
      const node = this.convertAttributeToFilterAttribute(
          field.id,
          form,
          alias
      );

      if (node) {
        nodes.push(node);
      }
    }

    return nodes;
  }


  // public static convertToJoinsGroup(attributeList: MetaDataJoinDto[]): FilterAttributeGroup[] {
  //     let filterJoins = [];
  //
  //     attributeList.forEach((entry: MetaDataJoinDto) => {
  //
  //             const attributeName: string = entry.attributeName ? entry.attributeName : null;
  //             const name: string = entry.name ? entry.name : null;
  //             const joinObjectTypeAlias: string = entry.joinObjectTypeAlias ? entry.joinObjectTypeAlias : null;
  //             const generic: boolean = entry.generic ? entry.generic : null;
  //             const joinObjectTypeId: number = entry.joinObjectTypeId ? entry.joinObjectTypeId : null;
  //             const joinRelationEnum: MetaDataJoinRelationEnum = entry.joinRelationEnum ? entry.joinRelationEnum : null;
  //
  //
  //             const filterAttributesWithValues = FilterAttribute.create(entry.attributeName, label, dataType, objectTypeAlias, objectTypeId, valueListEntries);
  //
  //             filterJoins.push(filterAttributesWithValues);
  //         }
  //     );
  //
  //     return [FilterAttributeGroup.create("Attributes", "Attributes", filterJoins)];
  //
  // }

  // public static createJoin(attribute: string, name: string, dataType: FilterAttributeDateTypeEnum, objectTypeAlias: string,objectTypeId:number, values: FilterAttributeValues[]): FilterAttribute {
  //     const filterAttribute = new FilterAttribute();
  //     filterAttribute.attribute = attribute;
  //     filterAttribute.dataType = dataType;
  //     filterAttribute.joinObjectTypeId = objectTypeId;
  //     filterAttribute.joinObjectTypeAlias = objectTypeAlias;
  //     filterAttribute.label = label;
  //     filterAttribute.values = values;
  //     filterAttribute.variables = [];
  //     return filterAttribute;
  // }
}
